import { ENV } from '@/config/environment';

type LogValue = unknown;

const REDACTED = '[redacted]';
const SENSITIVE_KEY = /(authorization|cookie|credential|email|latitude|longitude|payload|phone|secret|signature|subject|token)/i;
const BEARER_VALUE = /\bBearer\s+[A-Za-z0-9._~+/=-]+/gi;
const EMAIL_VALUE = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi;
const PHONE_VALUE = /\+?\d[\d\s().-]{7,}\d/g;
const SENSITIVE_QUERY_VALUE = /([?&](?:access_token|refresh_token|token|signature|secret|key|code|state)=)[^&#\s]*/gi;

function sanitizeString(value: string): string {
  if (ENV.IS_DEVELOPMENT) return value;

  const redacted = value
    .replace(BEARER_VALUE, 'Bearer [redacted]')
    .replace(EMAIL_VALUE, REDACTED)
    .replace(PHONE_VALUE, REDACTED)
    .replace(SENSITIVE_QUERY_VALUE, '$1[redacted]');

  return redacted.length <= 500 ? redacted : `${redacted.slice(0, 500)}…`;
}

function sanitize(value: LogValue, depth = 0, seen = new WeakSet<object>()): LogValue {
  if (value instanceof Error) {
    return {
      name: value.name,
      message: sanitizeString(value.message),
      ...(ENV.IS_DEVELOPMENT && value.stack ? { stack: value.stack } : {}),
    };
  }

  if (value === null || value === undefined || typeof value === 'boolean' || typeof value === 'number') {
    return value;
  }

  if (typeof value === 'string') {
    return sanitizeString(value);
  }

  if (typeof value !== 'object') return String(value);
  if (depth >= 3) return '[truncated]';
  if (seen.has(value)) return '[circular]';
  seen.add(value);

  if (Array.isArray(value)) {
    return value.slice(0, 20).map(item => sanitize(item, depth + 1, seen));
  }

  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).map(([key, item]) => [
      key,
      SENSITIVE_KEY.test(key) ? REDACTED : sanitize(item, depth + 1, seen),
    ]),
  );
}

function sanitized(values: LogValue[]): LogValue[] {
  return values.map(value => sanitize(value));
}

export const clientLogger = {
  debug: (...values: LogValue[]): void => {
    if (ENV.IS_DEVELOPMENT) console.debug(...values);
  },
  info: (...values: LogValue[]): void => {
    if (ENV.IS_DEVELOPMENT) console.info(...values);
  },
  warn: (...values: LogValue[]): void => {
    console.warn(...(ENV.IS_DEVELOPMENT ? values : sanitized(values)));
  },
  error: (...values: LogValue[]): void => {
    console.error(...(ENV.IS_DEVELOPMENT ? values : sanitized(values)));
  },
};
